import { createSelector } from "reselect";

import roles from "../policies/selectors/roles";

const rbacPermissions = {
  auditLogs: {
    view: ["admin", "auditor"]
  },
  config: {
    view: ["admin", "operator", "auditor"],
    edit: ["admin"]
  },
  services: {
    view: ["admin", "operator", "user", "auditor"],
    edit: ["admin", "operator"],
    register: ["admin", "operator"]
  },
  policies: {
    view: ["admin", "operator", "user", "auditor"],
    edit: ["admin", "operator", "user"]
  },
  users: {
    view: ["admin", "operator", "user", "auditor"],
    edit: ["admin", "operator", "user"]
  }
};

const user = {};

/**
 * Returns logged in user's role in RBAC.
 * @returns {Object} role
 */
user.getUserRoleInRbac = state => {
  const userRbacPolicy = state.policies.items.find(
    policy =>
      policy.service &&
      policy.service.$ref === "/api/rbac/v1/service/0" &&
      (policy.user && policy.user.$ref === state.user.$uri)
  );
  const userRbacRole = state.roles.items.find(
    role => userRbacPolicy && userRbacPolicy.role.$ref === role.$uri
  );

  return userRbacRole;
};

/**
 * Returns logged in user's groups' roles in RBAC.
 * @returns {Object[]} roles
 */
user.getGroupsRolesInRbac = state => {
  const groupRbacRoles = [];
  if (state.user && state.user.groups) {
    state.user.groups.forEach(group => {
      const groupRbacPolicy = state.policies.items.find(
        policy =>
          policy.service &&
          policy.service.$ref === "/api/rbac/v1/service/0" &&
          (policy.group && policy.group.$ref === group.$ref)
      );
      const groupRbacRole = state.roles.items.find(
        role => groupRbacPolicy && groupRbacPolicy.role.$ref === role.$uri
      );

      if (groupRbacRole) {
        groupRbacRoles.push(groupRbacRole);
      }
    });
  }
  return groupRbacRoles;
};

/**
 * Returns list of user's roles in RBAC (self and groups)
 * @param {Object} state
 * @returns {Object[]} roles
 */
user.getRolesInRbac = createSelector(
  [user.getUserRoleInRbac, user.getGroupsRolesInRbac],
  (userRbacRole, groupRbacRoles) =>
    userRbacRole ? [...groupRbacRoles, userRbacRole] : groupRbacRoles
);

/**
 * Returns list of roles logged in user can assign to newly created user in RBAC
 * @param {Object} state
 * @returns {Object[]} roles
 */
user.getRbacRolesUserCanAdd = createSelector(
  [user.getRolesInRbac, roles.get],
  (userRoles, roles) => {
    if (!userRoles) {
      return [];
    }

    const rbacRoles = userRoles.reduce((acc, userRole) => {
      if (userRole.label === "admin") {
        acc.push("admin", "auditor", "operator", "user");
      }
      if (userRole.label === "operator") {
        acc.push("auditor", "operator", "user");
      }
      if (userRole.label === "user") {
        acc.push("auditor", "user");
      }
      // Remove duplicates
      return Array.from(new Set(acc));
    }, []);

    return rbacRoles.map(rbacRole =>
      roles.find(role => role.label === rbacRole)
    );
  }
);

/**
 * Returns whether the logged in user can view audit logs in RBAC.
 * @param {Object} state
 * @returns {Boolean}
 */
user.canViewAuditLogs = createSelector(
  [user.getRolesInRbac],
  roles =>
    roles.some(role => rbacPermissions.auditLogs.view.includes(role.label))
);

/**
 * Returns whether the logged in user can add/edit/remove services in RBAC.
 * @param {Object} state
 * @returns {Boolean}
 */
user.canEditServices = createSelector(
  [user.getRolesInRbac],
  roles =>
    roles.some(role => rbacPermissions.services.edit.includes(role.label))
);

/**
 * Returns whether the logged in user can register services in RBAC.
 * @param {Object} state
 * @returns {Boolean}
 */
user.canRegisterServices = createSelector(
  [user.getRolesInRbac],
  roles =>
    roles.some(role => rbacPermissions.services.register.includes(role.label))
);

/**
 * Returns whether the logged in user can add/edit/remove policies in RBAC.
 * @param {Object} state
 * @returns {Boolean}
 */
user.canEditPolicies = createSelector(
  [user.getRolesInRbac],
  roles =>
    roles.some(role => rbacPermissions.policies.edit.includes(role.label))
);

/**
 * Returns whether the logged in user can add/edit/remove users in RBAC.
 * @param {Object} state
 * @returns {Boolean}
 */
user.canEditUsers = createSelector(
  [user.getRolesInRbac],
  roles => roles.some(role => rbacPermissions.users.edit.includes(role.label))
);

export { user as userSelectors, rbacPermissions };
